import { createReducer, createActions } from "reduxsauce";
import {
  REDUX_STATE,
  requestReducerFunc,
  failureReducerFunc,
  successReducerFunc,
  setReducerFunc,
} from "./redux-structure";
import { AppConstant, KeyConstant, SystemConstant } from "const";
import { convertString2JSON, toCamel } from "utils";
import { DISPLAY_MSG_TYPES } from "sagas/saga.helper";
const StorageUtil = window.electronUtils.storage;

/* ------------- Types and Action Creators ------------- */
const { Types, Creators } = createActions({
  updateMessageStatus: ["data", "prefixKey"],
  conversationSetLinkData: ["data", "prefixKey"],
  sendMessage: ["data", "prefixKey"], // Send local message to server
  sendFileMessage: ["data", "prefixKey"],
  receivedRemoteMessage: ["newMessage"], // Set new message from server
  getMessageDetail: ["sourceId", "prefixKey"], // Get/ save seen/ received member

  requestUploadImageCall: ["data", "prefixKey"],
  setSelectGroupId: ["data", "prefixKey"],
  checkBlockedContact: ["data", "prefixKey"], // data: {accountId: <Receiver's id>}
  markReadMessageInGroup: ["selectedGroupId", "threadingId"],
  clearNewMessage: ["categoryId", "deleteMessageIds"], // categoryId: group_id/ thread_id

  conversationSuccess: ["data"],
  conversationFailure: ["error", "data"],
  conversationSet: ["data"],

  pinMessage: ["data", "prefixKey"],
  synchPin: ["groupId", "prefixKey"],
});

export const ConversationTypes = Types;
export const ConversationActions = Creators;
export default Creators;

/* ------------- Initial State ------------- */
export const DEFAULT_ATTACHMENT = {
  processStatus: AppConstant.PROCESS_STATUS.nothing,
  attachmentId: null,
  sendType: null,
};
export const INITIAL_STATE = {
  ...REDUX_STATE,

  isSearchMode: false,
  isUpdateViewMode: 0, //  Trigger UI for current branch
  fetchSeenMember: 0, // Timestamp value is update when fetching seen member successfully
  updateMessageStatus: { updateMessageIds: [], modified: 0 },
  updateThreadStatus: { threadUpdate: [], modified: 0 },

  threadingId: null,
  selectedGroupId: null,

  uploadingAttachment: DEFAULT_ATTACHMENT,

  idGroupCallAvatar: null, // Id của cuộc gọi conference trong trường hợp init
  idAvatarEdit: null, // Id của cuộc gọi conference trong trường hợp còn lại
  linkData: {}, // Preview link message
  isAddConference: false, // True: User đang trong luồng taojh conference

  scrollToChildId: null, // source_id của message mà mình muốn nhảy đến
  blockedAccount: null, // Dành riêng cho trường hợp chat cá nhân và thằng đó bị blocked
  searchingMessage: {
    selectedMsgSourceId: null,
    messageSourceIdList: [],
    searchValue: "",
  },

  incomingMessages: {}, // {[group_id]: {<message_1_id>: <message_1>}, [thread_id]: {<message_1_id>: <message_1>}}
  changePinList: {}, // { timestamp, sourceMessageId, state }
  draftMessages: {}, // {[group_id]: <draft_message_group>, [thread_id]: <draft_message_thread>}}

  // Disappear message
  disappearTime: 0, // Trigger logic to handling disappear message
  disappearMsg: [], // save disappear message list

  // Delete only me
  deleteOnlyMeMsg: {
    message: null, // camelCase, message is being delete only me
    isDelete: true, // isDelete = false => restore message
  },

  quoteMessage: null,
  updateThreadIds: [],
  sendAsDirectMsgOpt: SystemConstant.THREAD_MSG_SEND_OPTION.notSendAsDirect,
};

/* ------------- Selector ------------- */
export const ConversationSelectors = {
  getSearchingMessage: state => state.conversationRedux.searchingMessage,
  getUploadingAttachment: state => state.conversationRedux.uploadingAttachment,
  getSelectedGroupId: state => state.conversationRedux.selectedGroupId,
  getThreadingId: state => state.conversationRedux.threadingId,
  getFetchSeenMember: state => state.conversationRedux.fetchSeenMember,
  getChangePinList: state => state.conversationRedux.changePinList,
  getDraftMessages: state => state.conversationRedux.draftMessages,
  getDisappearTime: state => state.conversationRedux.disappearTime,
  getDisappearMsg: state => state.conversationRedux.disappearMsg,
  getDeleteOnlyMeMsg: state => state.conversationRedux.deleteOnlyMeMsg,
  getQuoteMessage: state => state.conversationRedux.quoteMessage,
  getUpdateThreadIds: state => state.conversationRedux.updateThreadIds,
  getSendOpt: state => state.conversationRedux.sendAsDirectMsgOpt,
};

/* ------------- Reducers ------------- */
const request = (state = INITIAL_STATE) => requestReducerFunc({ ...state, fetchSeenMember: 0 });

const setLinkData = (state = INITIAL_STATE, action) => ({ ...state, linkData: { ...state.linkData, ...action.data } });

const setSelectGroupId = (state = INITIAL_STATE, action) => {
  const { threadingId, scrollToChildId, ...otherData } = action.data || {};
  const isChangeGroup = state.selectedGroupId !== otherData.selectedGroupId;
  // Clear queue message in redux
  if (threadingId) {
    delete state.incomingMessages[threadingId];
    delete state.incomingMessages[state.threadingId];
  } else if (isChangeGroup) {
    delete state.incomingMessages[otherData.selectedGroupId];
    delete state.incomingMessages[state.selectedGroupId];
  }

  return {
    ...state,
    updateThreadIds: [],
    isSearchMode: isChangeGroup ? false : state.isSearchMode,
    scrollToChildId: scrollToChildId || null,
    threadingId: threadingId || null,
    ...otherData,
  };
};

// Received remote message from server
const receivedRemoteMessage = (state = INITIAL_STATE, action) => {
  const newMessage = action.newMessage;
  return getStateWithNewMessage(newMessage, state);
};

// Send local message to server
const sendMessage = (state = INITIAL_STATE, action) => {
  const { currentMessage } = action.data;
  return getStateWithNewMessage(currentMessage, state);
};

// Handle new message and return new state
const getStateWithNewMessage = (newMessage, state = INITIAL_STATE) => {
  newMessage = toCamel(newMessage);
  if (!(newMessage && newMessage.id)) return state;

  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID);
  if (!newMessage.senderId && newMessage.sendType !== SystemConstant.SEND_TYPE.notFound)
    newMessage.senderId = accountId;

  // Trigger update disappearTimeRedux when sent my message
  const isMyMessage = newMessage.senderId === accountId;
  const messageOptions = newMessage.options ? convertString2JSON(newMessage.options, {}) : {};
  const { disappearing_f, time_to_live, send_as_direct_message } = messageOptions;
  if (isMyMessage && Boolean(newMessage.modified) && disappearing_f && Number(time_to_live) > 0) {
    state.disappearTime = newMessage.modified + Number(time_to_live);
  }

  if (Boolean(newMessage.modified) && Boolean(newMessage.disappearTime) && newMessage.disappearTime < Date.now())
    return state;

  if (newMessage.groupId === state.selectedGroupId && DISPLAY_MSG_TYPES.includes(newMessage.sendType)) {
    if (!newMessage.created) newMessage.created = Date.now();

    const incomingMessages = state.incomingMessages;
    const categoryId = newMessage.threadId || newMessage.groupId;
    const messageList = incomingMessages[categoryId] || {};

    if (send_as_direct_message === SystemConstant.THREAD_MSG_SEND_OPTION.sendAsDirect) {
      const groupCategory = newMessage.groupId;
      const currentGroupIncomingMsgs = incomingMessages[groupCategory] || {};

      return {
        ...state,
        incomingMessages: {
          ...incomingMessages,
          [groupCategory]: { ...currentGroupIncomingMsgs, [newMessage.id]: newMessage },
          [categoryId]: { ...messageList, [newMessage.id]: newMessage },
        },
      };
    } else {
      return {
        ...state,
        incomingMessages: {
          ...incomingMessages,
          [categoryId]: { ...messageList, [newMessage.id]: newMessage },
        },
      };
    }
  }

  return state;
};

const clearNewMessage = (state = INITIAL_STATE, action) => {
  const { categoryId, deleteMessageIds } = action;
  const incomingMessages = state.incomingMessages;
  const messageList = incomingMessages[categoryId];

  if (
    messageList &&
    Object.keys(messageList).length > 0 &&
    Array.isArray(deleteMessageIds) &&
    deleteMessageIds.length > 0
  ) {
    for (let index = 0; index < deleteMessageIds.length; index++) {
      const messageId = deleteMessageIds[index];
      delete messageList[messageId];
    }

    return {
      ...state,
      incomingMessages: {
        ...incomingMessages,
        [categoryId]: messageList,
      },
    };
  }

  return state;
};

const set = (state = INITIAL_STATE, action) => setReducerFunc(state, action);

const success = (state = INITIAL_STATE, action) => successReducerFunc(state, action);

const failure = (state = INITIAL_STATE, action) => failureReducerFunc(state, action);

/* ------------- Mapping ------------- */
export const HANDLERS = {
  [Types.UPDATE_MESSAGE_STATUS]: request,
  [Types.GET_MESSAGE_DETAIL]: request,

  [Types.SEND_MESSAGE]: sendMessage,
  [Types.SEND_FILE_MESSAGE]: sendMessage,
  [Types.CONVERSATION_SET_LINK_DATA]: setLinkData,
  [Types.RECEIVED_REMOTE_MESSAGE]: receivedRemoteMessage,
  [Types.CLEAR_NEW_MESSAGE]: clearNewMessage,

  [Types.REQUEST_UPLOAD_IMAGE_CALL]: request,
  [Types.SET_SELECT_GROUP_ID]: setSelectGroupId,
  [Types.CHECK_BLOCKED_CONTACT]: request,
  [Types.MARK_READ_MESSAGE_IN_GROUP]: request,

  [Types.CONVERSATION_SET]: set,
  [Types.CONVERSATION_SUCCESS]: success,
  [Types.CONVERSATION_FAILURE]: failure,

  [Types.PIN_MESSAGE]: request,
  [Types.SYNCH_PIN]: request,
};

/* ------------- Hookup Reducers To Types ------------- */
export const ConversationReducer = createReducer(INITIAL_STATE, HANDLERS);
